import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Gift, Trophy, Calendar, Star, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface LoyaltyBonus {
  id: string;
  bonus_type: string;
  bonus_amount: number;
  points_awarded: number;
  season_count: number;
  description: string;
  awarded_at: string;
  expires_at: string | null;
  is_claimed: boolean;
  claimed_at: string | null;
}

export const LoyaltyBonusDisplay = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [bonuses, setBonuses] = useState<LoyaltyBonus[]>([]);
  const [loading, setLoading] = useState(true);

  const user = supabaseUser || internalUser;
  const userType = supabaseUser ? 'supabase' : 'internal';

  useEffect(() => {
    if (user) {
      loadLoyaltyBonuses();
    }
  }, [user]);

  const loadLoyaltyBonuses = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('loyalty_bonuses')
        .select('*')
        .eq('user_id', user.id)
        .eq('user_type', userType)
        .order('awarded_at', { ascending: false });

      if (error) throw error;
      setBonuses(data || []);
    } catch (error) {
      console.error('Error loading loyalty bonuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimBonus = async (bonusId: string) => {
    try {
      const { error } = await supabase
        .from('loyalty_bonuses')
        .update({
          is_claimed: true,
          claimed_at: new Date().toISOString()
        })
        .eq('id', bonusId);

      if (error) throw error;

      toast.success('Bonus claimed successfully!');
      loadLoyaltyBonuses();
    } catch (error) {
      console.error('Error claiming bonus:', error);
      toast.error('Failed to claim bonus');
    }
  };

  const getBonusIcon = (type: string) => {
    switch (type) {
      case 'returning_user':
        return <Calendar className="w-5 h-5" />;
      case 'consecutive_seasons':
        return <Trophy className="w-5 h-5" />;
      case 'milestone':
        return <Star className="w-5 h-5" />;
      case 'early_bird':
        return <Zap className="w-5 h-5" />;
      case 'referral_streak':
        return <Gift className="w-5 h-5" />;
      default:
        return <Gift className="w-5 h-5" />;
    }
  };

  const getBonusTitle = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const unclaimedBonuses = bonuses.filter(b => !b.is_claimed && !isExpired(b.expires_at));
  const claimedBonuses = bonuses.filter(b => b.is_claimed);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading bonuses...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unclaimed Bonuses */}
      {unclaimedBonuses.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Available Bonuses</h3>
            <Badge variant="default">{unclaimedBonuses.length}</Badge>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {unclaimedBonuses.map((bonus) => (
              <Card key={bonus.id} className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg text-primary">
                        {getBonusIcon(bonus.bonus_type)}
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          {getBonusTitle(bonus.bonus_type)}
                        </CardTitle>
                        <CardDescription className="text-sm">
                          +{bonus.points_awarded} Locker Points
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="ml-2">New</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {bonus.description}
                  </p>
                  {bonus.season_count > 0 && (
                    <p className="text-xs text-muted-foreground mb-3">
                      <Trophy className="w-3 h-3 inline mr-1" />
                      {bonus.season_count} consecutive seasons
                    </p>
                  )}
                  <Button 
                    onClick={() => claimBonus(bonus.id)} 
                    className="w-full"
                    size="sm"
                  >
                    Claim Bonus
                  </Button>
                  {bonus.expires_at && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Expires: {new Date(bonus.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Claimed Bonuses History */}
      {claimedBonuses.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Bonus History</h3>
          <div className="space-y-2">
            {claimedBonuses.map((bonus) => (
              <Card key={bonus.id} className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-background rounded text-muted-foreground">
                        {getBonusIcon(bonus.bonus_type)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {getBonusTitle(bonus.bonus_type)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {bonus.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm">+{bonus.points_awarded}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(bonus.claimed_at!).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {bonuses.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center">
            <Gift className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No loyalty bonuses yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Keep participating to earn loyalty rewards!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
